import { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, TextField, Button, Box, List,
  ListItemText, Checkbox, FormControl, FormGroup, FormControlLabel, ListItemButton, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Persona, SuccessResponse_1OfUser, ModulePermission, PutPersonaRequest, PostPersonaRequest } from '../../../../app/services/api/generated';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import LeavePageModal from '../../../../components/shared/dialogs/LeavePageModal';
import { UserContext } from '../../../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import { transformForTranslation } from './RolesBox';
import TriStateCircle from './TriStateCircle';

interface Props {
  openModal: boolean;
  onCancel: () => void;
  persona?: Persona;
  onConfirm: (data: PutPersonaRequest | PostPersonaRequest) => void;
  modules: ModulePermission[];
}

const splitPermissionName = (name: string, moduleName: string) => name.replace(moduleName, '').replace(/([A-Z])/g, ' $1').trim();

const CreateUpdateRoleModal = ({ openModal, onCancel, persona, onConfirm, modules }: Props) => {
  const { logError } = useLogError();
  const theme = useTheme();
  const { getCurrentUser } = useNswagClient();
  const { user } = useContext(UserContext);
  const [retailer, setRetailer] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [request, setRequest] = useState<PutPersonaRequest | PostPersonaRequest>({ description: '', franchiseName: '', personaRoles: [] });
  const [initialRequest, setInitialRequest] = useState<PutPersonaRequest | PostPersonaRequest>({ description: '', franchiseName: '', personaRoles: [] });
  const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
  const [description, setDescription] = useState<string>(persona?.description ?? '');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  const systems = Array.from(new Set(modules.map(m => m.system || '')));
  const filteredModules = selectedSystem ? modules.filter(m => m.system === selectedSystem) : [];
  const selectedModuleObj = filteredModules.find(m => m.module === selectedModule);
  const filteredPermissions = selectedModuleObj ? selectedModuleObj.permissions : [];
  const userPermissions = user?.permissions || [];
  const allowedPermissions = filteredPermissions?.filter(p => userPermissions.includes(p)) || [];
  const { t } = useTranslation('manageRoles');

  useEffect(() => {
    const newRequest = {
      description: persona?.description,
      franchiseName: persona?.franchiseName,
      personaRoles: persona?.personaRoles,
    };

    setRequest(newRequest);
    setInitialRequest(newRequest);
    setDescription(persona?.description ?? '');
    setSelectedPermissions(persona?.personaRoles ?? []);
  }, [persona]);

  useEffect(() => {
    if (openModal) {
      getRetailer();
    }
  }, [openModal]);

  useEffect(() => {
    if (systems && systems.length > 0 && !selectedSystem) {
      setSelectedSystem(systems[0]);
    }
  }, [systems, selectedSystem]);

  useEffect(() => {
    if (filteredModules.length > 0 && !selectedModule) {
      setSelectedModule(filteredModules[0].module || '');
    }
  }, [filteredModules, selectedModule]);

  const getRetailer = () => {
    setIsLoading(true);

    getCurrentUser()
      .then((result: SuccessResponse_1OfUser) => {
        if (result?.data) {
          setRetailer(result.data.franchiseName ?? '');
        }
      })
      .catch((error) => logError(error))
      .finally(() => setIsLoading(false));
  };

  const isFormDirty = () => {
    return (
      description !== initialRequest.description ||
      JSON.stringify(selectedPermissions.sort()) !== JSON.stringify((initialRequest.personaRoles ?? []).sort())
    );
  };

  const handleCancel = () => {
    if (isFormDirty()) {
      setShowLeaveModal(true);
    } else {
      onCancel();
      setRequest({ description: '', franchiseName: '', personaRoles: [] });
      setDescription('');
      setSelectedPermissions([]);
    }
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setSelectedPermissions(prev => {
      let updated = [...prev];
      if (checked && !updated.includes(permission)) {
        updated.push(permission);
      } else if (!checked && updated.includes(permission)) {
        updated = updated.filter(p => p !== permission);
      }
      return updated;
    });
  };

  const handleConfirm = () => {
    onConfirm({
      ...request,
      description: description,
      franchiseName: retailer,
      personaRoles: selectedPermissions,
    });
    setRequest({ description: '', franchiseName: '', personaRoles: [] });
    setShowLeaveModal(false);
    setDescription('');
    setSelectedPermissions([]);
    onCancel();
  };

  // Check if the current user is assigned to the persona being edited
  const isCurrentUserPersona = persona && user && user.persona.id === persona.id;
  const isValid = description && description.trim() !== '' && selectedPermissions.length > 0 && !isCurrentUserPersona;

  // Returns 'all', 'some', or 'none' for a module based on selectedPermissions
  const getModuleTriState = (module: ModulePermission): 'all' | 'some' | 'none' => {
    if (!module.permissions || module.permissions.length === 0) return 'none';
    const total = module.permissions.length;
    const selected = module.permissions.filter(p => selectedPermissions.includes(p)).length;
    if (selected === 0) return 'none';
    if (selected === total) return 'all';
    return 'some';
  };

  // Returns 'all', 'some', or 'none' for a system based on all its modules' permissions and selectedPermissions
  const getSystemTriState = (system: string): 'all' | 'some' | 'none' => {
    const systemModules = modules.filter(m => m.system === system);
    let total = 0;
    let selected = 0;
    systemModules.forEach(module => {
      if (module.permissions && module.permissions.length > 0) {
        total += module.permissions.length;
        selected += module.permissions.filter(p => selectedPermissions.includes(p)).length;
      }
    });
    if (total === 0 || selected === 0) return 'none';
    if (selected === total) return 'all';
    return 'some';
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
      >
        <LoadingWheel />
      </Box>
    );
  }

  return (
    <>
      <Dialog
        maxWidth='lg'
        fullWidth
        onClose={handleCancel}
        open={openModal}
        sx={{
          '& .MuiDialog-paper': {
            py: '18px',
            px: '24px',
            maxHeight: '95vh',
          },
        }}
      >
        <Box>
          <DialogTitle sx={{
            fontSize: theme.typography.textLG.fontSize,
            fontWeight: theme.typography.fontWeightBold,
            px: 0,
          }}>
            {t('createUpdateRole')}
          </DialogTitle>
          <Typography variant="textMD"
            component="div"
            sx={{ mb: 4 }}>
            {t('editAddRole')}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{
          flexGrow: 1,
          padding: 0,
          overflow: 'hidden',
        }}>
          <Box component='div'
            display='flex'
            py='12px'>
            <TextField
              required
              id="role-name-input"
              label={t('name')}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 50 }}
              size='small'
              name='description'
              value={description}
              sx={{
                width: '320px',
                '& .MuiInputBase-root': {
                  height: '40px',
                  borderRadius: '4px',
                },
              }}
            />
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 4,
            flexGrow: 1,
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant='textSM'>{t('systems')}</Typography>
              <Box sx={{ overflow: 'auto', maxHeight: '280px' }}>
                <List sx={{ flexGrow: 1 }}>
                  {systems.map(system => (
                    <ListItemButton
                      key={system}
                      selected={selectedSystem === system}
                      onClick={() => {
                        setSelectedSystem(system);
                        setSelectedModule(null);
                      }}
                    >
                      <TriStateCircle
                        state={getSystemTriState(system)}
                      />
                      <ListItemText primary={t(transformForTranslation(system))} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='textSM'>{t('modules')}</Typography>
              <Box sx={{ overflow: 'auto', maxHeight: '280px' }}>
                <List sx={{ flexGrow: 1 }}>
                  {filteredModules.map(m => (
                    <ListItemButton
                      key={m.module}
                      selected={selectedModule === m.module}
                      onClick={() => setSelectedModule(m.module || '')}
                    >
                      <TriStateCircle
                        state={getModuleTriState(m)}
                      />
                      <ListItemText primary={t(transformForTranslation(m.module || ''))} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='textSM'>{t('permissions')}</Typography>
              <Box sx={{ overflow: 'auto', maxHeight: '280px' }}>
                {selectedModuleObj ? (
                  <FormControl component="fieldset"
                    sx={{ width: '100%', flexGrow: 1 }}>
                    <FormGroup sx={{ flexGrow: 1 }}>
                      {allowedPermissions.length > 0 ? allowedPermissions.map(permission => (
                        <FormControlLabel
                          key={permission}
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(permission)}
                              onChange={(_, checked) => handlePermissionChange(permission, checked)}
                            />
                          }
                          label={t(splitPermissionName(permission, selectedModuleObj?.module || '').toLowerCase()).replace(/\b\w/g, char => char.toLocaleUpperCase())}
                        />
                      )) : (
                        <Typography variant="textMD"
                          sx={{ p: 2, color: 'text.secondary' }}>
                          {t('noPermissions')}
                        </Typography>
                      )}
                    </FormGroup>
                  </FormControl>
                ) : (
                  <Typography variant="textMD"
                    sx={{ p: 2, color: 'text.secondary' }}>
                    {t('moduleSelection')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          {isCurrentUserPersona && (
            <Box sx={{ mb: 2 }}>
              <Typography color="error"
                variant="textMD">
                {t('cannotEditOwnRole', 'You cannot update or delete a role you are currently assigned to.')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button variant='secondary'
            size="lg"
            onClick={handleCancel}
            disabled={isCurrentUserPersona || false}
          >
            {t('cancel')}
          </Button>
          <Button
            variant='primary'
            size="lg"
            disabled={!isValid}
            onClick={handleConfirm}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      <LeavePageModal
        open={showLeaveModal}
        onConfirmUrl=""
        onClose={() => setShowLeaveModal(false)}
        onConfirm={() => setShowLeaveModal(false)}
        onCancel={() => {
          setShowLeaveModal(false);
          onCancel();
          setRequest({ description: '', franchiseName: '', personaRoles: [] });
          setDescription('');
          setSelectedPermissions([]);
        }}
      />
    </>
  );
};

export default CreateUpdateRoleModal;